/**
 * A line, and what it costs.
 *
 * This is the file the whole redesign turns on. In the Electron tree the same
 * arithmetic lived in three places — `kiosk-product-modal.tsx` computing a
 * display price, `kiosk-order-store.ts` summing a cart, and
 * `checkout-api.ts:buildCheckoutPayload` reconstructing a base price by
 * subtracting the change option and every add-on back out of the total,
 * because the cart had overwritten `productId` with the changed SKU. Three
 * derivations of one number, kept in agreement by hand.
 *
 * Here the *selection* is stored and the price is a pure function of it. There
 * is nothing to reconstruct because nothing was flattened.
 */
import type { AddOn, ChangeOption, OrderType, Product, Sku, Temperature } from './catalogue'
import { basePrice, findAddOn, findChangeOption, imageFor } from './catalogue'
import type { Money } from './money'
import { ZERO, add, sub, sum, times } from './money'

/**
 * What the customer chose. The durable half of a line — this is what is stored,
 * replayed and re-priced.
 */
export interface LineSelection {
  /** Stable within a cart, so a line can be addressed without an index. */
  readonly id: string
  readonly productId: Sku
  readonly quantity: number
  /** Undefined where the product offers no choice. */
  readonly temperature?: Temperature
  /** `code` of a `ChangeOption`, if one was taken. */
  readonly changeCode?: Sku
  /** `code`s of chosen add-ons. A code may repeat — two extra shots. */
  readonly addCodes: readonly Sku[]
  /** Overrides the order's type for this line. A coffee in, a cake to go. */
  readonly orderType?: OrderType
  /** Free text for the barista, printed on the cup slip. */
  readonly note?: string
}

export interface PricedAddOn {
  readonly addOn: AddOn
  readonly quantity: number
  readonly total: Money
}

/** The computed half. Never stored; always derived from a selection and a product. */
export interface PricedLine {
  readonly selection: LineSelection
  readonly product: Product

  /**
   * The SKU 1C is told about.
   *
   * Precedence, ported from `kiosk-product-modal.tsx:executeAdd`: a change
   * option wins, then an iced choice with an `iceSku`, then the product's own.
   * Stored on the order once computed, because a receipt reprinted next month
   * must say what the check said.
   */
  readonly effectiveSku: Sku

  readonly changeOption: ChangeOption | null
  readonly addOns: readonly PricedAddOn[]

  /** The menu price of the drink, before any promotion. Per unit. */
  readonly listPrice: Money
  /** After the product's own promotion. Per unit. Add-ons are not discounted. */
  readonly discountedPrice: Money
  /**
   * What 1C calls `selling_price`: the drink plus the change option, because a
   * change option moves the *line's* SKU and its price belongs on the line.
   * Add-ons stay in `adds[]`. Per unit.
   */
  readonly sellingPrice: Money
  /** The same, after the promotion. Per unit. */
  readonly discountedSellingPrice: Money
  /** Every add-on on one unit of this line. */
  readonly addOnsPrice: Money
  /** What one of this line costs the customer: discounted selling + add-ons. */
  readonly unitPrice: Money

  readonly total: Money
  /** What the promotion took off, across the whole line. Never negative. */
  readonly savings: Money
}

/**
 * A key that says whether two selections are the same line.
 *
 * `kiosk-order-store.ts:addToCart` merged on `productId` alone, so a latte with
 * an extra shot and a latte without collapsed into one line at whichever price
 * was added first. That is a real defect and this is the fix: two selections
 * merge only when everything the customer chose is identical.
 *
 * Add codes are sorted so «vanilla then oat» and «oat then vanilla» are one
 * line, and a note is part of the key because two cups with different names on
 * them are two cups.
 */
export function lineKey(selection: Omit<LineSelection, 'id' | 'quantity'>): string {
  return JSON.stringify([
    selection.productId,
    selection.temperature ?? null,
    selection.changeCode ?? null,
    [...selection.addCodes].sort(),
    selection.orderType ?? null,
    selection.note ?? null
  ])
}

export function effectiveSkuOf(product: Product, selection: Pick<LineSelection, 'changeCode' | 'temperature'>): Sku {
  if (selection.changeCode !== undefined) {
    const change = findChangeOption(product, selection.changeCode)
    if (change !== undefined) return change.code
  }
  if (selection.temperature === 'ice' && product.iceSku !== undefined) return product.iceSku
  return product.sku
}

/** Collapse repeated add-on codes into `{ code, quantity }`, preserving order. */
function groupAddCodes(codes: readonly Sku[]): { code: Sku; quantity: number }[] {
  const order: Sku[] = []
  const counts = new Map<Sku, number>()
  for (const code of codes) {
    if (!counts.has(code)) order.push(code)
    counts.set(code, (counts.get(code) ?? 0) + 1)
  }
  return order.map((code) => ({ code, quantity: counts.get(code)! }))
}

export class UnknownSelection extends Error {
  constructor(
    readonly kind: 'add-on' | 'change-option',
    readonly code: Sku,
    readonly productId: Sku
  ) {
    super(`${kind} ${code} is not offered by product ${productId}`)
    this.name = 'UnknownSelection'
  }
}

/**
 * Price one line.
 *
 * Refuses a code the product does not offer rather than ignoring it. A silently
 * dropped add-on is a customer charged for a drink they did not get, and the
 * only place it could be caught is here — by the time a checkout payload is
 * built, the code is gone.
 */
export function priceLine(product: Product, selection: LineSelection): PricedLine {
  if (selection.quantity < 1 || !Number.isInteger(selection.quantity)) {
    throw new RangeError(`line quantity must be a positive whole number, got ${selection.quantity}`)
  }

  let changeOption: ChangeOption | null = null
  if (selection.changeCode !== undefined) {
    const found = findChangeOption(product, selection.changeCode)
    if (found === undefined) throw new UnknownSelection('change-option', selection.changeCode, product.id)
    changeOption = found
  }

  const addOns: PricedAddOn[] = groupAddCodes(selection.addCodes).map(({ code, quantity }) => {
    const addOn = findAddOn(product, code)
    if (addOn === undefined) throw new UnknownSelection('add-on', code, product.id)
    return { addOn, quantity, total: times(addOn.price, quantity) }
  })

  const listPrice = product.price
  const discountedPrice = basePrice(product)
  const changePrice = changeOption?.price ?? ZERO
  const addOnsPrice = sum(addOns.map((a) => a.total))

  const sellingPrice = add(listPrice, changePrice)
  const discountedSellingPrice = add(discountedPrice, changePrice)
  const unitPrice = add(discountedSellingPrice, addOnsPrice)

  return {
    selection,
    product,
    effectiveSku: effectiveSkuOf(product, selection),
    changeOption,
    addOns,
    listPrice,
    discountedPrice,
    sellingPrice,
    discountedSellingPrice,
    addOnsPrice,
    unitPrice,
    total: times(unitPrice, selection.quantity),
    savings: times(sub(sellingPrice, discountedSellingPrice), selection.quantity)
  }
}

/** What this line is called on a screen and on a receipt. */
export function lineLabel(line: PricedLine, locale: 'mn' | 'en' = 'mn'): string {
  const { product, changeOption, selection } = line
  const base = locale === 'en' && product.nameEn ? product.nameEn : product.name
  const name = selection.temperature === 'ice' && product.iceName ? product.iceName : base
  return changeOption === null ? name : `${name} · ${changeOption.label}`
}

/**
 * The picture this line shows, wherever it is drawn.
 *
 * `lineLabel`'s counterpart, and it exists for the same reason: the basket and
 * the modal were each deciding what an iced latte looks like, and one of them
 * would eventually decide differently.
 */
export function lineImage(line: PricedLine): string | undefined {
  return imageFor(line.product, line.selection.temperature)
}

/** The add-ons as one line of text, for a receipt column too narrow for a list. */
export function addOnsLabel(line: PricedLine): string {
  return line.addOns.map((a) => (a.quantity > 1 ? `${a.addOn.label} ×${a.quantity}` : a.addOn.label)).join(', ')
}

/**
 * How many more of this line the customer may add.
 *
 * `Infinity` where the product is unlimited, which is most of a coffee menu —
 * stock tracking on the feed is for the food case.
 */
export function remainingStock(product: Product, alreadyInCart: number): number {
  if (product.isInfinite) return Number.POSITIVE_INFINITY
  return Math.max(0, product.balance - alreadyInCart)
}
